import { useMergeRefs } from "@floating-ui/react"
import { cloneElement, Fragment, isValidElement, useMemo } from "react"
import type { HTMLProps, ReactElement, Ref } from "react"
import { useTooltipContext } from "./useTooltipContext"

interface TooltipTriggerProps extends HTMLProps<HTMLElement> {
	ref?: Ref<HTMLElement>
}

export const TooltipTrigger = ({ children, ref: propRef, ...props }: TooltipTriggerProps) => {
	const context = useTooltipContext()
	const { floating, interactions, open: isOpen } = context

	// with multiple children, we can filter out falsy values (e.g. null) due to conditional rendering
	// to correctly check for a single child which we can use directly as the trigger
	const validArrayChildren = Array.isArray(children) && children.filter(Boolean)
	const singleArrayChild = validArrayChildren && validArrayChildren.length === 1 ? validArrayChildren[0] : null

	const isCloneableElement =
		(isValidElement(children) && children.type !== Fragment) || isValidElement(singleArrayChild)
	const element = isCloneableElement
		? ((singleArrayChild ?? children) as ReactElement<Record<string, unknown> & { ref?: Ref<unknown> }>)
		: null

	// Preserve the child's own ref (e.g. a react-hook-form field ref used to focus/scroll to it) by merging it
	// with the floating-ui reference ref instead of overwriting it, since this trigger may wrap the same
	// child regardless of whether tooltip content is currently present.
	const elementRef = element?.props.ref
	const mergedRef = useMergeRefs([floating.refs.setReference, propRef, elementRef])

	const dataState = isOpen ? "open" : "closed"

	// Build reference props once to avoid repeated calls
	const referenceProps = useMemo(() => interactions.getReferenceProps(props), [interactions, props])

	// If possible, don't create an extra wrapper element, Fragment check is needed for e.g., CheckboxGroup
	if (element) {
		// Clone element and add props
		// Passing callback refs (from useMergeRefs) to cloneElement is safe because they are functions, not ref objects.
		// The type checker incorrectly flags this as accessing a ref during render, but callback refs don't have .current
		// eslint-disable-next-line react-hooks/refs
		return cloneElement(element, {
			...element.props,
			...referenceProps,
			"data-state": dataState,
			ref: mergedRef,
		})
	}

	return (
		<span ref={mergedRef} data-state={dataState} {...referenceProps}>
			{children}
		</span>
	)
}
